import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/public/SEO";
import { ArrowRight } from "lucide-react";

const HERO = "/media/img/cave-interior.jpg";
const IMG_VALLEY = "/media/img/valley-sunset.jpg";
const IMG_DOOR = "/media/img/cave-door.jpg";

const FAQ = [
  { q: "Capella Cave ne zaman açılacak?", a: "İlkbahar 2028 itibarıyla kapılarımız ilk Kurucu Misafirlerimize açılacak." },
  { q: "Otelin konumu nedir?", a: "Göreme, Nevşehir — Kapadokya'nın kalbinde. Nevşehir Kapadokya Havalimanı'na yaklaşık 40 dakika uzaklıktadır." },
  { q: "Kaç odanız var?", a: "14 farklı butik mağara oda. Hiçbiri diğerine benzemez; her birinin kendi mimarisi vardır." },
  { q: "Rezervasyon ne zaman başlayacak?", a: "Önce Kurucu Misafir Listemizdekilerle iletişime geçeceğiz. Genel rezervasyon 2028 başında açılacak." },
  { q: "Capella Cave'in konsepti nedir?", a: "Anadolu'nun kadim mağara hayatını çağdaş bir misafirperverlik diliyle yeniden yorumlayan, sessiz lüks anlayışında butik bir konaklama deneyimi." },
];

export default function CapellaCaveVisionPage() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title="Capella Cave Vizyonu"
        description="Capella Cave, 2028 ilkbaharında Göreme'de açılacak butik bir mağara otel. Mağara yaşamından ilham alan tasarım vizyonu, sessiz lüks ve doğal mimari yaklaşımı."
        ogImage={HERO}
        jsonLd={faqSchema}
      />

      {/* HERO — Cinematic */}
      <section className="relative min-h-screen overflow-hidden grain" data-testid="hotel-hero">
        <video
          key={isMobile ? "mobile" : "desktop"}
          autoPlay muted loop playsInline preload="metadata"
          poster={HERO}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hotel-hero-video"
        >
          <source
            src={isMobile ? "/media/video/hero-mobile.mp4" : "/media/video/hero-desktop.mp4"}
            type="video/mp4"
          />
        </video>
        {/* Overlay stack — readability for headline + CTA */}
        <div className="absolute inset-0 bg-gradient-to-b from-cc-dark/40 via-cc-dark/10 to-cc-dark/75 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 50% at top left, rgba(20,15,12,0.82) 0%, rgba(20,15,12,0.38) 45%, transparent 75%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 65% 60% at bottom left, rgba(20,15,12,0.85) 0%, rgba(20,15,12,0.4) 45%, transparent 78%)",
          }}
        />
        <div className="relative min-h-screen max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center text-cc-bg pt-32 pb-20">
          <div className="cc-reveal max-w-3xl">
            <div className="text-[10px] tracking-[0.32em] uppercase text-cc-bg/80 mb-8 flex items-center gap-3">
              <span className="w-10 h-px bg-cc-sepia" /> Açılış 2028 · Göreme · Kapadokya
            </div>
            <h1 className="font-serif font-light text-5xl md:text-7xl lg:text-[6.5rem] leading-[1.02] mb-10">
              Bir kapı,<br/><span className="italic">bin yıl önce</span>.
            </h1>
            <p className="text-cc-bg/85 text-base md:text-xl max-w-2xl leading-[1.7] mb-12 font-light">
              Capella Cave, Göreme'nin yumuşak tüfüne sekiz yıllık bir restorasyonla oyulmuş 14 odalı butik bir mağara otel.
              Henüz açılmadık — ama Kurucu Misafir Listemize katılanlar ilk kapıdan girecek olanlar.
            </p>
            <Link to="/kurucu-misafir-listesi" data-testid="hotel-hero-cta" className="cc-btn cc-btn-light">
              Kurucu Misafir Listesi <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CAPELLA CAVE NEDİR */}
      <section className="py-28 md:py-40 max-w-6xl mx-auto px-6 md:px-12" data-testid="hotel-what">
        <div className="grid md:grid-cols-12 gap-14 items-start">
          <div className="md:col-span-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">Capella Cave Nedir?</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-cc-text leading-[1.05]">
              Bir otel değil.<br/><span className="italic">Bir yaklaşım.</span>
            </h2>
          </div>
          <div className="md:col-span-7 text-cc-muted text-base md:text-lg leading-[1.85] space-y-6 md:pt-3">
            <p>
              Capella Cave, Anadolu'nun en eski coğrafyalarından birinde, çağdaş bir misafirperverlik dilini
              binlerce yıllık bir hafızayla buluşturmayı amaçlayan bir konaklama vizyonudur.
              Göreme'nin sırtındaki yumuşak tüfün içinde, 14 odadan oluşur.
            </p>
            <p>
              Restorasyonumuz sekiz yıldır sürüyor. Taşı kesmedik; yumuşakça temizledik. Tüfün içine modern bir konfor
              katmanı yerleştirdik ama orijinal mağaranın hiçbir hissini yok etmedik. Bir odaya girdiğinizde
              bin yıl önceki bir Bizans rahibinin oradan geçtiğini hayal edebilirsiniz.
            </p>
          </div>
        </div>
      </section>

      {/* NEDEN KAPADOKYA */}
      <section className="relative py-28 md:py-40 bg-cc-dark text-cc-bg overflow-hidden grain" data-testid="hotel-why">
        <div className="absolute inset-0 opacity-30">
          <img src={IMG_VALLEY} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-14 items-center">
          <div className="md:col-span-5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">Neden Kapadokya?</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-cc-bg leading-[1.05]">
              Dünyada başka<br/><span className="italic">bir tane yok.</span>
            </h2>
          </div>
          <div className="md:col-span-7 text-cc-bg/85 text-base md:text-lg leading-[1.85] space-y-6">
            <p>
              Kapadokya, dünyanın hiçbir yerinde benzeri olmayan bir coğrafyadır. Volkanik tüf, milyonlarca yılda
              rüzgâr ve suyla yontuldu. İnsan, bu tüfün içine evler, kiliseler, manastırlar oydu. Yeraltı şehirleri
              tek bir mevsimde inşa edilmedi; nesiller boyu çoğaltıldı.
            </p>
            <p>
              Capella Cave bu hafızanın üzerine bir tabaka ekleme cesaretidir. Yeni bir bina değil; süregelen bir
              hikâyenin bir cümlesi olmaktır.
            </p>
          </div>
        </div>
      </section>

      {/* TASARIM VİZYONU */}
      <section className="py-28 md:py-40 max-w-6xl mx-auto px-6 md:px-12" data-testid="hotel-design">
        <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">Tasarım Vizyonu</div>
        <h2 className="font-serif text-4xl md:text-6xl text-cc-text leading-[1.05] mb-16 max-w-3xl">
          Mağara yaşamından ilham alan,<br/><span className="italic">sessiz bir lüks.</span>
        </h2>

        <div className="grid md:grid-cols-3 gap-10 md:gap-14">
          {[
            { t: "Doğal Malzeme", d: "Tüf, ahşap, deri, keten ve el dokuması yün. Hiçbir oda sentetik bir yüzey taşımaz." },
            { t: "Sessizlik Mimarisi", d: "Mağara, dış sıcaklıktan ve sesten bağımsız bir iklim sunar. 18-22°C, sıfır şehir gürültüsü." },
            { t: "Kişiye Özel Detay", d: "Her odanın kendi sesi, kendi havası, kendi kokusu var. Personel size adınızla seslenir." },
          ].map((d) => (
            <div key={d.t} className="border-t border-cc-border pt-8">
              <h3 className="font-serif text-2xl text-cc-text mb-4">{d.t}</h3>
              <p className="text-cc-muted leading-relaxed">{d.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 14 ODA */}
      <section className="py-28 md:py-36 max-w-7xl mx-auto px-6 md:px-12" data-testid="hotel-rooms">
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={IMG_DOOR} alt="Capella Cave mağara oda kapısı" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">14 Oda</div>
            <h2 className="font-serif text-4xl md:text-6xl text-cc-text leading-[1.05] mb-8">
              14 oda, <span className="italic">14 hikâye.</span>
            </h2>
            <div className="text-cc-muted leading-[1.85] space-y-5">
              <p>Hiçbir oda bir diğerine benzemez. Bir kısmı yere açılır kemerli pencereleriyle Aşk Vadisi'ne bakar.
                 Bir kısmı yalnızca yıldızlarla görüşür.</p>
              <p>İki oda eski bir manastırın yan koridorlarıdır; üç oda ise bir Selçuklu konağının asıl odalarıdır.
                 Her odanın kendi hikâyesi, kendi rüzgârı vardır.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SESSIZ LÜKS QUOTE */}
      <section className="py-28 md:py-36 bg-cc-dark/[0.03] border-y border-cc-border">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">Tasarım Manifesto</div>
          <blockquote className="font-serif italic text-3xl md:text-5xl text-cc-text leading-[1.2] mb-8">
            "Lüks gösteriş değildir.<br/>Lüks, görünmez olmaktır.<br/>Bir taşın size konuşmasına izin vermektir."
          </blockquote>
          <p className="text-[10px] tracking-[0.28em] uppercase text-cc-muted">Capella Cave Tasarım Manifesto</p>
        </div>
      </section>

      {/* KURUCU MİSAFİR CTA */}
      <section className="py-28 md:py-36 max-w-5xl mx-auto px-6 md:px-12 text-center" data-testid="hotel-founding-cta">
        <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-6">Açılış Öncesi</div>
        <h2 className="font-serif text-4xl md:text-6xl text-cc-text leading-[1.05] mb-8">
          Önce siz girersiniz.
        </h2>
        <p className="text-cc-muted max-w-2xl mx-auto text-base md:text-lg leading-[1.85] mb-12">
          Capella Cave Kurucu Misafir Listesi'ne katılarak açılış öncesi öncelikli rezervasyon, %20'ye varan
          açılış indirimi ve isminize ayrılmış özel deneyimler kazanın. Yalnızca 100 isim.
        </p>
        <Link to="/kurucu-misafir-listesi" className="cc-btn" data-testid="hotel-founding-cta-btn">
          Kurucu Misafir Listesi'ne Katıl <ArrowRight size={14} />
        </Link>
      </section>

      {/* SSS */}
      <section className="py-24 md:py-32 max-w-3xl mx-auto px-6 md:px-12" data-testid="hotel-faq">
        <div className="text-[10px] tracking-[0.28em] uppercase text-cc-sepia mb-4 text-center">Sık Sorulanlar</div>
        <h2 className="font-serif text-3xl md:text-4xl text-cc-text mb-12 text-center">Capella Cave Hakkında</h2>
        <div className="space-y-8">
          {FAQ.map((f, i) => (
            <div key={i} className="pb-8 border-b border-cc-border last:border-0">
              <h3 className="font-serif text-xl text-cc-text mb-3">{f.q}</h3>
              <p className="text-cc-muted leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
